import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Check,
  CheckCheck,
  ChevronLeft,
  Clock,
  Copy,
  ExternalLink,
  Eye,
  FileText,
  Flame,
  Inbox,
  MessageSquare,
  PanelRightOpen,
  Search,
  StickyNote,
  TimerOff,
  X,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import { toast } from "sonner";
import {
  CLOSE_REASON_LABEL,
  CONVERSATION_SCOPE_LABEL,
  CONVERSATION_SCOPES,
  type CloseReason,
  type Paginated,
} from "@rn/contracts";
import { INTENT_LABEL, LEAD_STATUS_LABEL, LEAD_TEMPERATURE_LABEL } from "@rn/contracts";
import { AiPanel } from "@/components/crm/AiPanel";
import { ChannelAlert } from "@/components/crm/ChannelAlert";
import { ChatScheduling } from "@/components/crm/ChatScheduling";
import { ChatStage } from "@/components/crm/ChatStage";
import { LiveQueue } from "@/components/crm/LiveQueue";
import { TypechatStatus } from "@/components/flows/TypechatStatus";
import { Composer, type ComposerHandle } from "@/components/crm/Composer";
import {
  CloseDialog,
  NovaConversaDialog,
  PresenceToggle,
  PrioritySelect,
  TransferDialog,
} from "@/components/crm/ConversationActions";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Hint } from "@/components/ui/tooltip";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  addressLabel,
  channelLabel,
  formatPhone,
  horaCurta,
  isSocialChannel,
  relativeTime,
} from "@/lib/crm";

export const Route = createFileRoute("/_authenticated/conversas")({
  // `?c=<id>` abre uma conversa direto. É o que permite a ficha do lead levar
  // para cá sem a pessoa ter que procurar o nome na fila — e o que faz o
  // endereço da barra continuar valendo quando alguém o copia para um colega.
  validateSearch: (search: Record<string, unknown>): { c?: string } =>
    typeof search["c"] === "string" ? { c: search["c"] } : {},
  component: ConversasPage,
});

type Conversation = {
  id: string;
  channel: string;
  address: string;
  status: string;
  priority: string;
  protocol: number | null;
  unreadCount: number;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  windowOpen: boolean;
  windowMinutesLeft: number;
  leadId: string | null;
  lastIntent: string | null;
  lastOutboundAt: string | null;
  leadStatus: string | null;
  leadTemperature: string | null;
  /** Quem mais está com esta conversa aberta agora. */
  viewers: string | null;
  closeReason: string | null;
  contact: {
    id: string;
    fullName: string | null;
    phoneNormalized: string | null;
    /** Respondeu SAIR no WhatsApp. Só o detalhe da conversa traz. */
    whatsappOptOutAt?: string | null;
  } | null;
  assignee: { id: string; fullName: string | null } | null;
  team?: { id: string; name: string } | null;
  /** Por qual número da empresa a conversa entrou (e é respondida). */
  channelAccount?: { id: string | null; label: string | null } | null;
};

/** Um número de WhatsApp da empresa ("Salvador", "Feira"). */
type NumeroDaEmpresa = {
  id: string;
  provider: string;
  label: string | null;
  accountKey: string | null;
  status: string;
  readOnly: boolean;
  primary: boolean;
};

/**
 * Como chamar o número na tela.
 *
 * Só existe quando a empresa tem MAIS de um: com um número só, um selo
 * "Salvador" em toda linha gastaria espaço sem informar nada.
 */
function rotuloDoNumero(
  c: Pick<Conversation, "channelAccount">,
  numeros: NumeroDaEmpresa[],
): string | null {
  if (numeros.length < 2 || !c.channelAccount?.id) return null;
  const n = numeros.find((x) => x.id === c.channelAccount!.id);
  return c.channelAccount.label ?? n?.label ?? n?.accountKey ?? null;
}

type Message = {
  id: string;
  direction: "inbound" | "outbound" | "internal";
  messageType: string;
  body: string | null;
  mediaUrl: string | null;
  mediaMimeType: string | null;
  status: string;
  templateName: string | null;
  failedReason: string | null;
  occurredAt: string;
  sentBy: { id: string; fullName: string | null } | null;
};

/** Como chamar a pessoa, com ou sem cadastro. */
function nomeDe(c: Pick<Conversation, "contact" | "channel" | "address">): string {
  return c.contact?.fullName ?? addressLabel(c.channel, c.address);
}

/**
 * Central de atendimento.
 *
 * Três colunas porque o atendente precisa das três ao mesmo tempo: a fila para
 * escolher, a conversa para responder e a ficha para saber com quem está
 * falando. Alternar entre telas para isso é o que faz o atendimento demorar.
 *
 * A tela tem altura fixa e nunca rola inteira: cada coluna rola por dentro.
 * Com a página rolando, a caixa de escrita sumia para baixo assim que a
 * conversa passava de uma tela — e quem atende passa o dia com ela aberta.
 *
 * A atualização é por consulta periódica curta, não WebSocket. Um socket
 * exigiria estado de conexão, reconexão e autenticação próprios; cinco segundos
 * de atraso numa fila de atendimento é imperceptível.
 */
function ConversasPage() {
  const queryClient = useQueryClient();
  const { c: conversaDaUrl } = Route.useSearch();
  /**
   * O filtro escolhido — ou nenhum, e aí o padrão é calculado.
   *
   * "Minhas" parece o padrão óbvio e estava errado: numa clínica onde ninguém
   * assume conversa, ele mostra uma tela vazia em cima de uma caixa com mais de
   * cem pessoas esperando. Tela vazia por escolha de padrão é o pior primeiro
   * contato possível — parece sistema quebrado, e a reação natural é fechar.
   */
  const [escolhido, setScope] = useState<(typeof CONVERSATION_SCOPES)[number] | null>(null);
  const [priority, setPriority] = useState("");
  /** Filtro por número da empresa — só aparece quando há mais de um. */
  const [numero, setNumero] = useState("");
  /**
   * Atender e acompanhar são o mesmo assunto, então moram na mesma tela.
   *
   * A caixa de entrada é o padrão porque é onde o trabalho acontece; a fila ao
   * vivo responde outra pergunta — carga por atendente e quem já passou do
   * limite —, que interessa a quem supervisiona, não a quem está no meio de
   * uma conversa.
   */
  const [visao, setVisao] = useState<"caixa" | "fila">("caixa");
  /**
   * A ficha da paciente no celular.
   *
   * Em tela grande ela fica na terceira coluna, sempre visível. No celular não
   * há três colunas: empilhar lista, conversa e ficha faria a pessoa rolar a
   * conversa inteira para chegar ao botão de registrar venda. Aqui ela vira um
   * segundo painel, alcançado por um toque.
   */
  const [fichaAberta, setFichaAberta] = useState(false);
  const [selected, setSelected] = useState<string | null>(conversaDaUrl ?? null);
  /**
   * Filtro de texto da fila, aplicado ao que já está no navegador.
   *
   * Não vai ao servidor: a lista carregada já responde "cadê a Maria?" na
   * hora, sem uma consulta por tecla. O limite é honesto — só acha quem está
   * nas linhas carregadas — e por isso o rodapé continua dizendo quantas há.
   */
  const [busca, setBusca] = useState("");

  /**
   * A caixa de escrita, alcançada de fora.
   *
   * Mora aqui, e não na conversa, porque o assistente de IA fica na ficha (a
   * terceira coluna) e precisa colocar o rascunho na caixa — e só isso. O
   * envio continua sendo o botão de sempre.
   */
  const caixa = useRef<ComposerHandle>(null);

  const summary = useQuery({
    queryKey: ["conversations", "summary"],
    queryFn: () => api.get<Record<string, number>>("/conversations/summary"),
    refetchInterval: 15_000,
  });

  /**
   * Enquanto ninguém escolheu, o filtro segue o que existe de verdade.
   *
   * Valor derivado, e não estado dentro de efeito: com efeito, a tela mostraria
   * "Minhas" vazia por um instante antes de corrigir, e esse piscar é
   * exatamente a impressão de sistema quebrado que se quer evitar.
   *
   * Quem tem conversa própria começa nas suas; quem não tem começa em TODAS —
   * e não num recorte de urgência. Abrir num filtro estreito faz a caixa
   * parecer menor do que é, e a primeira reação de quem vê 12 linhas onde há
   * 113 conversas é achar que o sistema perdeu gente. Os recortes continuam a
   * um clique, com o número do lado em cada chip.
   */
  const resumo = summary.data;
  const scope = escolhido ?? (!resumo ? "mine" : resumo["mine"] ? "mine" : "all");

  /**
   * Quantas linhas a fila mostra.
   *
   * Cresce sob demanda em vez de já pedir tudo: uma caixa com milhares de
   * conversas travaria a tela para mostrar o que ninguém vai rolar. O que não
   * pode acontecer — e acontecia — é a lista parar em 50 sem dizer nada, com
   * mais de sessenta conversas invisíveis atrás do fim da rolagem.
   */
  const [limite, setLimite] = useState(50);

  const numerosQuery = useQuery({
    queryKey: ["channel-accounts"],
    queryFn: () => api.get<NumeroDaEmpresa[]>("/conversations/channel-accounts"),
    staleTime: 60_000,
  });
  const numeros = numerosQuery.data ?? [];

  const list = useQuery({
    queryKey: ["conversations", scope, priority, numero, limite],
    queryFn: () =>
      api.get<Paginated<Conversation>>("/conversations", {
        scope,
        pageSize: limite,
        ...(priority ? { priority } : {}),
        ...(numero ? { integrationId: numero } : {}),
      }),
    refetchInterval: 10_000,
  });

  const mostrando = list.data?.data.length ?? 0;
  const total = list.data?.pagination.total ?? 0;

  const conversa = useQuery({
    queryKey: ["conversation", selected],
    queryFn: () => api.get<Conversation>(`/conversations/${selected}`),
    enabled: selected !== null,
  });

  const messages = useQuery({
    queryKey: ["conversation", selected, "messages"],
    queryFn: () => api.get<Message[]>(`/conversations/${selected}/messages`),
    enabled: selected !== null,
    refetchInterval: 5_000,
  });

  // Abrir a conversa zera o não-lido. Só uma vez por seleção: repetir a cada
  // atualização geraria uma escrita a cada cinco segundos.
  const markRead = useMutation({
    mutationFn: (id: string) => api.patch(`/conversations/${id}`, { markRead: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["conversations"] }),
  });

  useEffect(() => {
    if (selected) markRead.mutate(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  /**
   * Avisa a equipe em qual conversa eu estou.
   *
   * O batimento repetido não é exagero: é o que faz a marca expirar sozinha.
   * Quem fecha a aba, perde a conexão ou deixa o computador não avisa ninguém,
   * e sem renovação a conversa ficaria marcada como ocupada para sempre — o
   * resto da equipe desviaria dela justamente quando não há mais ninguém lá.
   *
   * A limpeza ao trocar de conversa manda `null` porque sair é tão informativo
   * quanto entrar: a conversa volta a ficar livre na hora, sem esperar o
   * relógio.
   */
  useEffect(() => {
    const avisar = (id: string | null) =>
      api.put("/attendance/presence/viewing", { conversationId: id }).catch(() => {
        // Presença é conforto, não regra: falhar aqui não pode atrapalhar
        // quem está no meio de um atendimento.
      });

    if (!selected) return;

    void avisar(selected);
    const timer = setInterval(() => void avisar(selected), 30_000);

    return () => {
      clearInterval(timer);
      void avisar(null);
    };
  }, [selected]);

  // Filtro local por nome, telefone ou trecho da última mensagem.
  const termo = busca.trim().toLowerCase();
  const digitos = termo.replace(/\D/g, "");
  const linhas = useMemo(() => {
    const todas = list.data?.data ?? [];
    if (!termo) return todas;
    return todas.filter((c) => {
      const nome = nomeDe(c).toLowerCase();
      const fone = c.contact?.phoneNormalized ?? c.address;
      const previa = (c.lastMessagePreview ?? "").toLowerCase();
      return (
        nome.includes(termo) ||
        previa.includes(termo) ||
        (digitos.length >= 3 && fone.includes(digitos))
      );
    });
  }, [list.data, termo, digitos]);

  const abrir = (id: string) => {
    setSelected(id);
    setFichaAberta(false);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* ── Cabeçalho de uma linha ─────────────────────────────────────── */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2.5 sm:px-6">
        <h1 className="font-display text-xl font-semibold tracking-tight">Conversas</h1>

        <Tabs
          variant="pills"
          defaultValue="caixa"
          onValueChange={(v) => setVisao(v === "fila" ? "fila" : "caixa")}
        >
          <TabsList>
            <TabsTrigger value="caixa">Caixa de entrada</TabsTrigger>
            <TabsTrigger value="fila">Fila ao vivo</TabsTrigger>
          </TabsList>
        </Tabs>

        <ChannelAlert compact />

        <div className="ml-auto flex items-center gap-2">
          <PresenceToggle />
          <NovaConversaDialog onOpened={abrir} />
        </div>
      </div>

      {visao === "fila" && <LiveQueue />}

      {/* Escondido, e não desmontado: voltar para a caixa devolve a mesma
          conversa aberta e a mesma rolagem. Desmontar faria quem foi conferir
          a fila perder o lugar onde estava — no meio de um atendimento, isso
          custa mais que as consultas que seguem rodando em segundo plano. */}
      <div className={cn("flex min-h-0 flex-1", visao === "fila" && "hidden")}>
        {/* ── Fila ───────────────────────────────────────────────────────── */}
        <aside
          className={cn(
            "min-h-0 w-full flex-col lg:w-80 lg:shrink-0 lg:border-r",
            selected ? "hidden lg:flex" : "flex",
          )}
        >
          <div className="shrink-0 space-y-2 border-b px-3 pb-2.5 pt-3">
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Filtrar por nome ou telefone"
                  aria-label="Filtrar conversas"
                  className="h-8 pl-8 pr-7 text-xs"
                />
                {busca && (
                  <button
                    type="button"
                    aria-label="Limpar filtro"
                    onClick={() => setBusca("")}
                    className="absolute right-1.5 top-1/2 inline-flex size-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X className="size-3" />
                  </button>
                )}
              </div>
              <Select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                aria-label="Prioridade"
                className="w-28 shrink-0 [&>select]:h-8 [&>select]:text-xs"
              >
                <option value="">Prioridade</option>
                <option value="urgent">Urgente</option>
                <option value="high">Alta</option>
                <option value="normal">Normal</option>
                <option value="low">Baixa</option>
              </Select>
              {numeros.length > 1 && (
                <Select
                  value={numero}
                  onChange={(e) => setNumero(e.target.value)}
                  aria-label="Número"
                  className="w-28 shrink-0 [&>select]:h-8 [&>select]:text-xs"
                >
                  <option value="">Número</option>
                  {numeros.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.label ?? n.accountKey ?? "Sem nome"}
                    </option>
                  ))}
                </Select>
              )}
            </div>

            {/* Os recortes como chips com o número ao lado: dá para ver de
                relance quantas nunca foram respondidas sem trocar de filtro. */}
            <div className="scrollbar-none -mx-3 flex gap-1.5 overflow-x-auto px-3">
              {CONVERSATION_SCOPES.map((s) => {
                const n = summary.data?.[s] ?? 0;
                const ativo = scope === s;
                const alerta = s === "pending" && n > 0;
                return (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={ativo}
                    onClick={() => setScope(s)}
                    className={cn(
                      "inline-flex h-7 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-medium transition-colors",
                      ativo
                        ? "bg-primary text-primary-foreground shadow-card"
                        : alerta
                          ? "bg-destructive/10 text-destructive hover:bg-destructive/15"
                          : "bg-muted text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {CONVERSATION_SCOPE_LABEL[s]}
                    {n > 0 && (
                      <span
                        className={cn(
                          "kpi rounded-full px-1.5 text-[10px] leading-4",
                          ativo ? "bg-primary-foreground/20" : "bg-card shadow-card",
                        )}
                      >
                        {n}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {list.isLoading ? (
              <FilaSkeleton />
            ) : list.isError ? (
              <EmptyState
                compact
                icon={AlertTriangle}
                title="Não foi possível carregar a fila"
                description="Confira a conexão e tente de novo."
                action={
                  <Button variant="outline" size="sm" onClick={() => void list.refetch()}>
                    Tentar de novo
                  </Button>
                }
              />
            ) : linhas.length === 0 ? (
              <EmptyState
                compact
                icon={Inbox}
                title={termo ? "Nada com esse filtro" : "Nada por aqui"}
                description={
                  termo
                    ? "Só as conversas já carregadas entram na busca. Limpe o filtro ou carregue mais."
                    : "Nenhuma conversa neste recorte. Troque o chip acima ou abra uma nova conversa."
                }
              />
            ) : (
              <ul className="divide-y divide-border/60">
                {linhas.map((c) => (
                  <li key={c.id}>
                    <LinhaFila
                      conversa={c}
                      ativa={selected === c.id}
                      onClick={() => abrir(c.id)}
                      numero={rotuloDoNumero(c, numeros)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* O rodapé conta a verdade sobre o tamanho da caixa. Sem ele, a
              lista que para no fim da rolagem parece a caixa inteira — e foi
              exatamente assim que sessenta e três conversas sumiram da vista. */}
          {total > 0 && (
            <div className="flex shrink-0 items-center justify-between gap-2 border-t px-4 py-2 text-xs text-muted-foreground">
              <span className="tabular-nums">
                {termo ? `${linhas.length} de ${mostrando} carregadas` : `${mostrando} de ${total}`}
              </span>
              {mostrando < total && (
                <Button
                  variant="ghost"
                  size="xs"
                  disabled={list.isFetching}
                  onClick={() => setLimite((l) => l + 50)}
                >
                  {list.isFetching ? "Carregando…" : "Carregar mais"}
                </Button>
              )}
            </div>
          )}
        </aside>

        {/* ── Conversa ───────────────────────────────────────────────────── */}
        <section
          className={cn(
            "min-h-0 min-w-0 flex-1 flex-col bg-muted/20",
            selected ? "flex" : "hidden lg:flex",
            fichaAberta && "hidden lg:flex",
          )}
        >
          {selected === null ? (
            <EmptyState
              icon={MessageSquare}
              title="Escolha uma conversa à esquerda"
              description="A conversa abre aqui e a ficha da paciente, ao lado. Tudo o que você precisa para responder fica numa tela só."
              className="flex-1"
            />
          ) : (
            <Thread
              conversation={conversa.data}
              messages={messages.data ?? []}
              loading={messages.isLoading}
              erro={messages.isError}
              conversationId={selected}
              caixa={caixa}
              onVoltar={() => setSelected(null)}
              onAbrirFicha={() => setFichaAberta(true)}
              numero={conversa.data ? rotuloDoNumero(conversa.data, numeros) : null}
            />
          )}
        </section>

        {/* ── Ficha ──────────────────────────────────────────────────────── */}
        <aside
          className={cn(
            "min-h-0 w-full flex-col bg-muted/30 lg:w-80 lg:shrink-0 lg:border-l xl:w-96",
            fichaAberta && selected ? "flex" : "hidden lg:flex",
          )}
        >
          {/* Barra só de celular: é o caminho de volta para a conversa. */}
          <div className="flex shrink-0 items-center gap-2 border-b bg-background px-3 py-2 lg:hidden">
            <Button
              variant="ghost"
              size="sm"
              className="-ml-1"
              onClick={() => setFichaAberta(false)}
            >
              <ChevronLeft />
              Conversa
            </Button>
            <span className="truncate text-sm font-semibold">
              {conversa.data ? nomeDe(conversa.data) : "Ficha"}
            </span>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 sm:p-4">
            {conversa.data ? (
              <ContactPanel
                conversation={conversa.data}
                caixa={caixa}
                onRascunhoUsado={() => setFichaAberta(false)}
              />
            ) : selected ? (
              <FichaSkeleton />
            ) : (
              <EmptyState
                compact
                icon={Inbox}
                title="Nenhuma conversa aberta"
                description="A ficha de quem você estiver atendendo aparece aqui."
              />
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ── Fila ───────────────────────────────────────────────────────────────── */

function FilaSkeleton() {
  return (
    <ul className="divide-y divide-border/60">
      {Array.from({ length: 7 }, (_, i) => (
        <li key={i} className="flex gap-3 px-3 py-3">
          <Skeleton className="size-9 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2 py-0.5">
            <div className="flex items-center justify-between gap-2">
              <Skeleton className="h-3.5 w-32" />
              <Skeleton className="h-3 w-10" />
            </div>
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-4 w-24 rounded-full" />
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Uma conversa na fila.
 *
 * O que dá para saber sem abrir: quem é, o que disse por último, há quanto
 * tempo, quantas mensagens esperam, o que ela quer, se alguém já respondeu e
 * quem está com ela agora. Tudo isso em três linhas — mais que isso e a fila
 * deixa de caber na tela.
 */
function LinhaFila({
  conversa: c,
  ativa,
  onClick,
  numero,
}: {
  conversa: Conversation;
  ativa: boolean;
  onClick: () => void;
  /** Nome do número da empresa; nulo quando ela tem um só. */
  numero: string | null;
}) {
  const nome = nomeDe(c);
  const temperatura = c.leadTemperature && c.leadTemperature !== "cold" ? c.leadTemperature : null;
  const etapa = c.leadStatus && c.leadStatus !== "new" ? c.leadStatus : null;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={ativa ? "true" : undefined}
      className={cn(
        "relative flex w-full cursor-pointer gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/50",
        ativa &&
          "bg-primary/8 before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-primary hover:bg-primary/8",
      )}
    >
      <Avatar name={c.contact?.fullName ?? null} seed={c.contact?.phoneNormalized ?? c.address} />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <PriorityDot priority={c.priority} />
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-sm leading-tight",
              c.unreadCount > 0 ? "font-semibold" : "font-medium",
            )}
          >
            {nome}
          </span>
          {c.lastMessageAt && (
            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
              {relativeTime(c.lastMessageAt).replace("há ", "")}
            </span>
          )}
        </div>

        <div className="mt-0.5 flex items-center gap-2">
          <p
            className={cn(
              "min-w-0 flex-1 truncate text-xs",
              c.unreadCount > 0 ? "text-foreground/80" : "text-muted-foreground",
            )}
          >
            {c.lastMessagePreview ?? "—"}
          </p>
          {c.unreadCount > 0 && (
            <span className="kpi inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
              {c.unreadCount}
            </span>
          )}
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {/* A etiqueta some assim que respondemos: ela existe para dizer
              "esta pessoa quer algo AGORA", e uma etiqueta que fica depois de
              atendida vira enfeite. */}
          {c.lastIntent && c.unreadCount > 0 && <IntentTag intent={c.lastIntent} />}

          {!c.lastOutboundAt && <Badge variant="danger">Nunca respondida</Badge>}

          {/* Quem está aqui agora. Evita o desperdício clássico de duas
              pessoas responderem a mesma paciente enquanto ninguém responde a
              seguinte. */}
          {c.viewers && (
            <Badge variant="info">
              <Eye />
              {c.viewers} está aqui
            </Badge>
          )}

          {/* A etapa do funil aqui evita perguntar de novo o que a paciente
              já respondeu — e mostra, sem abrir nada, que esta conversa já
              virou agendamento ou venda. */}
          {etapa && (
            <Badge variant="muted">
              {LEAD_STATUS_LABEL[etapa as keyof typeof LEAD_STATUS_LABEL] ?? etapa}
            </Badge>
          )}
          {temperatura && (
            <Badge variant="muted">
              {(temperatura === "hot" || temperatura === "very_hot") && <Flame />}
              {LEAD_TEMPERATURE_LABEL[temperatura as keyof typeof LEAD_TEMPERATURE_LABEL] ??
                temperatura}
            </Badge>
          )}

          {/* O canal só aparece quando NÃO é WhatsApp: escrever "WhatsApp"
              em 95% das linhas gasta espaço e não informa. O que precisa
              saltar é a exceção. */}
          {isSocialChannel(c.channel) && <Badge variant="outline">{channelLabel(c.channel)}</Badge>}
          {/* Qual número da clínica a pessoa procurou: a resposta sai por ele. */}
          {numero && <Badge variant="outline">{numero}</Badge>}
          {c.channel === "whatsapp_evolution" && (
            <span
              className="text-[10px] text-muted-foreground/70"
              title="Sem atribuição de anúncio"
            >
              não oficial
            </span>
          )}
          {!c.windowOpen && (
            <Badge variant="muted">
              <TimerOff />
              janela fechada
            </Badge>
          )}

          {/* O responsável na própria linha: sem isso, saber de quem é a
              conversa exige abrir uma por uma. */}
          <span className="ml-auto inline-flex items-center">
            {c.assignee ? (
              <Hint label={c.assignee.fullName ?? "Responsável"} side="left">
                <span>
                  <Avatar name={c.assignee.fullName ?? "?"} seed={c.assignee.id} size="xs" />
                </span>
              </Hint>
            ) : (
              <span className="text-[10px] text-muted-foreground/70">sem dono</span>
            )}
          </span>
        </div>
      </div>
    </button>
  );
}

/**
 * O que a pessoa quis, lido da última mensagem dela.
 *
 * Cor por urgência comercial, não por estética: quem quer agendar e quem achou
 * caro aparecem em atenção porque são os dois momentos em que a demora custa a
 * venda. Dúvida e endereço ficam no tom neutro — são interesse, não decisão.
 */
function IntentTag({ intent }: { intent: string }) {
  const rotulo = INTENT_LABEL[intent];
  if (!rotulo) return null;

  const quente = intent === "agendar" || intent === "preco" || intent === "objecao";

  return <Badge variant={quente ? "warning" : "soft"}>{rotulo}</Badge>;
}

/** Urgente e alta ganham cor; o resto não precisa gritar. */
function PriorityDot({ priority }: { priority: string }) {
  if (priority === "urgent") {
    return <span className="size-2 shrink-0 rounded-full bg-destructive" title="Urgente" />;
  }
  if (priority === "high") {
    return <span className="size-2 shrink-0 rounded-full bg-warning" title="Alta" />;
  }
  return null;
}

/* ── Conversa ───────────────────────────────────────────────────────────── */

/** A janela de 24h do WhatsApp, num relance. */
function JanelaBadge({
  conversation,
  className,
}: {
  conversation: Pick<Conversation, "windowOpen" | "windowMinutesLeft">;
  className?: string;
}) {
  if (conversation.windowOpen) {
    const min = conversation.windowMinutesLeft;
    const restante = min >= 60 ? `${Math.floor(min / 60)}h` : `${min} min`;
    return (
      <Badge variant="success" className={className}>
        <Clock />
        janela aberta · {restante}
      </Badge>
    );
  }
  return (
    <Badge variant="warning" className={className}>
      <TimerOff />
      janela fechada
    </Badge>
  );
}

function mesmoDia(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** "Hoje", "Ontem" ou a data curta — o rótulo do separador de dia. */
function diaRotulo(iso: string, hoje: Date): string {
  const d = new Date(iso);
  if (mesmoDia(d, hoje)) return "Hoje";
  const ontem = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 1);
  if (mesmoDia(d, ontem)) return "Ontem";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    ...(d.getFullYear() !== hoje.getFullYear() ? { year: "numeric" } : {}),
  }).format(d);
}

/** Mensagens do mesmo lado em menos de cinco minutos formam um bloco só. */
const JANELA_DE_AGRUPAMENTO_MS = 5 * 60_000;

function Thread({
  conversation,
  messages,
  loading,
  erro,
  conversationId,
  caixa,
  onVoltar,
  onAbrirFicha,
  numero,
}: {
  /** Nome do número da empresa; nulo quando ela tem um só. */
  numero: string | null;
  conversation: Conversation | undefined;
  messages: Message[];
  loading: boolean;
  erro: boolean;
  conversationId: string;
  caixa: RefObject<ComposerHandle | null>;
  onVoltar: () => void;
  onAbrirFicha: () => void;
}) {
  const fim = useRef<HTMLDivElement>(null);

  // Relógio lido uma vez por montagem, para os rótulos "Hoje"/"Ontem": ler
  // `new Date()` a cada renderização é impuro para o React, e a conversa é
  // reaberta ao trocar de pessoa de qualquer forma.
  const [hoje] = useState(() => new Date());

  useEffect(() => {
    fim.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  return (
    <>
      {/* Cabeçalho da conversa: quem é, por onde veio e o que dá para fazer
          com ela sem sair daqui. */}
      <header className="glass flex shrink-0 items-center gap-2 border-b px-3 py-2 sm:gap-3 sm:px-4">
        <Hint label="Voltar para a fila" side="bottom">
          <Button
            variant="ghost"
            size="icon-sm"
            className="-ml-1 lg:hidden"
            aria-label="Voltar para a fila"
            onClick={onVoltar}
          >
            <ChevronLeft />
          </Button>
        </Hint>

        {conversation ? (
          <>
            <Avatar
              name={conversation.contact?.fullName ?? null}
              seed={conversation.contact?.phoneNormalized ?? conversation.address}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold leading-tight">{nomeDe(conversation)}</p>
              <p className="truncate text-xs text-muted-foreground">
                {conversation.contact?.phoneNormalized
                  ? formatPhone(conversation.contact.phoneNormalized)
                  : addressLabel(conversation.channel, conversation.address)}
                {isSocialChannel(conversation.channel) &&
                  ` · ${channelLabel(conversation.channel)}`}
                {numero && ` · WhatsApp ${numero}`}
                {conversation.protocol !== null && ` · nº ${conversation.protocol}`}
              </p>
            </div>

            {conversation.status === "closed" ? (
              <Badge variant="muted" className="hidden sm:inline-flex">
                Encerrada
              </Badge>
            ) : (
              <JanelaBadge conversation={conversation} className="hidden sm:inline-flex" />
            )}

            <TypechatStatus conversationId={conversation.id} />

            <PrioritySelect
              conversationId={conversation.id}
              value={conversation.priority}
              className="hidden w-28 md:inline-flex [&>select]:h-8 [&>select]:text-xs"
            />

            <div className="flex shrink-0 items-center">
              <TransferDialog conversationId={conversation.id} trigger="icon" />
              <CloseDialog
                conversationId={conversation.id}
                aberta={conversation.status !== "closed"}
                trigger="icon"
              />
              <Hint label="Abrir ficha" side="bottom">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="lg:hidden"
                  aria-label="Abrir ficha"
                  onClick={onAbrirFicha}
                >
                  <PanelRightOpen />
                </Button>
              </Hint>
            </div>
          </>
        ) : (
          <div className="flex flex-1 items-center gap-3">
            <Skeleton className="size-9 rounded-full" />
            <div className="space-y-1.5">
              <Skeleton className="h-3.5 w-36" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4 pt-1 sm:px-5">
        {loading ? (
          <ThreadSkeleton />
        ) : erro ? (
          <EmptyState
            compact
            icon={AlertTriangle}
            title="Não foi possível carregar as mensagens"
            description="A próxima atualização automática tenta de novo em alguns segundos."
          />
        ) : messages.length === 0 ? (
          <EmptyState
            compact
            icon={MessageSquare}
            title="Sem mensagens"
            description="A conversa começa quando você escrever, ou quando a pessoa mandar a primeira mensagem."
          />
        ) : (
          messages.map((m, i) => {
            const anterior = i > 0 ? messages[i - 1] : undefined;
            const rotulo = diaRotulo(m.occurredAt, hoje);
            const novoDia = !anterior || diaRotulo(anterior.occurredAt, hoje) !== rotulo;
            const continuacao =
              !novoDia &&
              anterior !== undefined &&
              anterior.direction === m.direction &&
              new Date(m.occurredAt).getTime() - new Date(anterior.occurredAt).getTime() <
                JANELA_DE_AGRUPAMENTO_MS;
            return (
              <Fragment key={m.id}>
                {novoDia && <SeparadorDeDia rotulo={rotulo} />}
                <Bubble message={m} continuacao={continuacao} />
              </Fragment>
            );
          })
        )}
        <div ref={fim} />
      </div>

      {/* A pessoa respondeu SAIR. A equipe ainda pode escrever (é atendimento,
          não disparo), mas quem escreve precisa saber disso antes. */}
      {conversation?.contact?.whatsappOptOutAt && conversation.channel.startsWith("whatsapp") && (
        <div
          role="status"
          className="mx-3 mb-2 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning-foreground dark:text-warning"
        >
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>
            Este contato pediu para não receber mensagens automáticas (respondeu SAIR em{" "}
            {new Date(conversation.contact.whatsappOptOutAt).toLocaleDateString("pt-BR")}). As
            automações não escrevem para ele; responda só se for atendimento que ele pediu.
          </span>
        </div>
      )}

      <Composer
        ref={caixa}
        conversationId={conversationId}
        windowOpen={conversation?.windowOpen ?? true}
      />
    </>
  );
}

function ThreadSkeleton() {
  return (
    <div className="space-y-3 pt-3">
      <div className="flex justify-center">
        <Skeleton className="h-5 w-14 rounded-full" />
      </div>
      <div className="flex justify-start">
        <Skeleton className="h-14 w-3/5 rounded-2xl rounded-bl-md" />
      </div>
      <div className="flex justify-end">
        <Skeleton className="h-10 w-2/5 rounded-2xl rounded-br-md" />
      </div>
      <div className="flex justify-start">
        <Skeleton className="h-20 w-1/2 rounded-2xl rounded-bl-md" />
      </div>
      <div className="flex justify-end">
        <Skeleton className="h-14 w-3/5 rounded-2xl rounded-br-md" />
      </div>
    </div>
  );
}

function SeparadorDeDia({ rotulo }: { rotulo: string }) {
  return (
    <div className="my-3 flex items-center justify-center">
      <span className="rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground">
        {rotulo}
      </span>
    </div>
  );
}

function Bubble({ message, continuacao }: { message: Message; continuacao: boolean }) {
  // Nota interna não é "minha" nem "dela": é do time. Fica no meio, com cara
  // diferente, para ninguém confundir com o que a paciente leu.
  if (message.direction === "internal") {
    return (
      <div className={cn("flex justify-center", continuacao ? "mt-1" : "mt-3")}>
        <div className="max-w-[85%] rounded-2xl bg-warning/18 px-3.5 py-2 text-sm">
          <p className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-warning-foreground dark:text-warning">
            <StickyNote className="size-3" />
            Nota interna
            {message.sentBy?.fullName ? ` · ${message.sentBy.fullName}` : ""}
          </p>
          <p className="whitespace-pre-wrap break-words">{message.body}</p>
          <p className="mt-1 text-right text-[10px] tabular-nums text-muted-foreground">
            {horaCurta(message.occurredAt)}
          </p>
        </div>
      </div>
    );
  }

  const meu = message.direction === "outbound";
  const falhou = message.status === "failed";

  return (
    <div
      className={cn("flex", meu ? "justify-end" : "justify-start", continuacao ? "mt-1" : "mt-3")}
    >
      <div
        className={cn(
          "max-w-[85%] px-3.5 py-2 text-sm sm:max-w-[72%]",
          // A minha em tinta clara da cor principal, e não sólida: o texto
          // continua na cor do corpo, legível nos dois temas, e a diferença de
          // lado já diz quem falou.
          meu
            ? "rounded-2xl rounded-br-md bg-primary/12 text-foreground"
            : "rounded-2xl rounded-bl-md border bg-card shadow-card",
          falhou && "border border-destructive/40 bg-destructive/10",
        )}
      >
        {message.templateName && (
          <p className="mb-1 inline-flex items-center gap-1 rounded-full bg-background/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <FileText className="size-3" />
            Template · {message.templateName}
          </p>
        )}

        {message.mediaUrl && <Media message={message} />}

        {message.body && <p className="whitespace-pre-wrap break-words">{message.body}</p>}
        {!message.body && !message.mediaUrl && (
          <p className="italic text-muted-foreground">[{message.messageType}]</p>
        )}

        <p className="mt-1 flex items-center justify-end gap-1 text-[10px] tabular-nums text-muted-foreground">
          {meu && message.sentBy?.fullName && (
            <span className="truncate">{message.sentBy.fullName.split(" ")[0]} ·</span>
          )}
          {horaCurta(message.occurredAt)}
          {meu && !falhou && <StatusIcon status={message.status} />}
        </p>
        {falhou && (
          <p className="mt-1 flex items-start gap-1 text-xs font-medium text-destructive">
            <AlertTriangle className="mt-0.5 size-3 shrink-0" />
            {message.failedReason ?? "Não foi entregue"}
          </p>
        )}
      </div>
    </div>
  );
}

/** O nome do arquivo, lido do fim da URL — o que a paciente reconhece. */
function nomeDoArquivo(url: string): string {
  try {
    const ultimo = new URL(url, "http://x").pathname.split("/").pop() ?? "";
    return decodeURIComponent(ultimo) || "Abrir arquivo";
  } catch {
    return "Abrir arquivo";
  }
}

/**
 * Mídia na conversa.
 *
 * Áudio ganha player nativo porque, numa clínica, metade do que chega é áudio —
 * obrigar a baixar o arquivo para ouvir é o mesmo que não mostrar. Documento
 * vira link, e não visualizador embutido: PDF de exame abre melhor na aba do
 * navegador do que numa moldura de 300 pixels.
 */
function Media({ message }: { message: Message }) {
  const tipo = message.mediaMimeType ?? "";
  const url = message.mediaUrl!;

  if (tipo.startsWith("image/")) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="mb-1.5 block">
        <img
          src={url}
          alt={message.body ?? "Imagem enviada"}
          loading="lazy"
          className="max-h-72 w-auto max-w-full rounded-xl object-cover"
        />
      </a>
    );
  }

  if (tipo.startsWith("audio/")) {
    return <audio controls src={url} className="mb-1.5 w-64 max-w-full" />;
  }

  if (tipo.startsWith("video/")) {
    return <video controls src={url} className="mb-1.5 max-h-72 max-w-full rounded-xl" />;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="mb-1.5 inline-flex max-w-full items-center gap-2 rounded-lg border bg-background/70 px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
    >
      <FileText className="size-4 shrink-0 text-muted-foreground" />
      <span className="truncate">{nomeDoArquivo(url)}</span>
    </a>
  );
}

/** Um tique enviado, dois entregue, dois azuis lido — a convenção do WhatsApp. */
function StatusIcon({ status }: { status: string }) {
  if (status === "read") {
    return (
      <span title="Lida" className="inline-flex text-info">
        <CheckCheck className="size-3.5" />
      </span>
    );
  }
  if (status === "delivered") {
    return (
      <span title="Entregue" className="inline-flex">
        <CheckCheck className="size-3.5" />
      </span>
    );
  }
  if (status === "sent") {
    return (
      <span title="Enviada" className="inline-flex">
        <Check className="size-3.5" />
      </span>
    );
  }
  return (
    <span title="Enviando" className="inline-flex">
      <Clock className="size-3" />
    </span>
  );
}

/* ── Ficha ──────────────────────────────────────────────────────────────── */

function FichaSkeleton() {
  return (
    <>
      <Skeleton className="h-36 rounded-xl" />
      <Skeleton className="h-40 rounded-xl" />
      <Skeleton className="h-44 rounded-xl" />
    </>
  );
}

/**
 * A ficha de quem está do outro lado, em cartões: contato, atendimento, etapa
 * do funil, agendamento e o assistente. Rola sozinha dentro da coluna.
 */
function ContactPanel({
  conversation,
  caixa,
  onRascunhoUsado,
}: {
  conversation: Conversation;
  caixa: RefObject<ComposerHandle | null>;
  /** No celular, usar o rascunho leva de volta para a conversa. */
  onRascunhoUsado?: () => void;
}) {
  const queryClient = useQueryClient();

  const people = useQuery({
    queryKey: ["users", "assignable"],
    queryFn: () =>
      api.get<{ id: string; fullName: string | null; email: string }[]>("/users/assignable"),
  });

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/conversations/${conversation.id}`, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["conversation", conversation.id] });
      await queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Não foi possível salvar"),
  });

  const telefone = conversation.contact?.phoneNormalized ?? null;

  async function copiarTelefone() {
    if (!telefone) return;
    try {
      await navigator.clipboard.writeText(formatPhone(telefone));
      toast.success("Telefone copiado");
    } catch {
      toast.error("Não foi possível copiar. Selecione o número e copie à mão.");
    }
  }

  return (
    <>
      {/* ── Contato ─────────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-start gap-3">
            <Avatar
              name={conversation.contact?.fullName ?? null}
              seed={telefone ?? conversation.address}
              size="lg"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-base font-semibold leading-tight">
                {conversation.contact?.fullName ?? "Sem nome"}
              </p>
              <div className="mt-0.5 flex items-center gap-0.5 text-sm text-muted-foreground">
                <span className="truncate tabular-nums">
                  {telefone
                    ? formatPhone(telefone)
                    : addressLabel(conversation.channel, conversation.address)}
                </span>
                {telefone && (
                  <Hint label="Copiar telefone">
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Copiar telefone"
                      className="text-muted-foreground"
                      onClick={() => void copiarTelefone()}
                    >
                      <Copy />
                    </Button>
                  </Hint>
                )}
              </div>
              {conversation.protocol !== null && (
                <p className="text-xs text-muted-foreground">
                  Protocolo <span className="font-mono">{conversation.protocol}</span>
                </p>
              )}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
            <JanelaBadge conversation={conversation} />
            {conversation.status === "closed" && (
              <Badge variant="muted">
                Encerrada
                {conversation.closeReason
                  ? ` · ${CLOSE_REASON_LABEL[conversation.closeReason as CloseReason] ?? conversation.closeReason}`
                  : ""}
              </Badge>
            )}
            {isSocialChannel(conversation.channel) && (
              <Badge variant="info">{channelLabel(conversation.channel)}</Badge>
            )}
            {conversation.channel === "whatsapp_evolution" && (
              <Badge variant="outline">canal não oficial</Badge>
            )}
          </div>

          <TypechatStatus conversationId={conversation.id} variant="panel" className="mt-3" />

          {isSocialChannel(conversation.channel) && (
            <p className="mt-3 rounded-lg bg-info/12 px-2.5 py-2 text-xs leading-relaxed text-foreground/80">
              Veio por <strong>{channelLabel(conversation.channel)}</strong>. A pessoa ainda não deu
              telefone nem e-mail — peça na conversa para conseguir falar com ela depois que a
              janela de 24 horas fechar.
            </p>
          )}

          {conversation.channel === "whatsapp_evolution" && (
            <p className="mt-3 rounded-lg bg-warning/18 px-2.5 py-2 text-xs leading-relaxed text-foreground/80">
              Canal não oficial: esta conversa <strong>não tem atribuição de anúncio</strong>. A
              Evolution não recebe o <code>ctwa_clid</code> que a Meta anexa a um Click-to-WhatsApp.
            </p>
          )}

          {(conversation.leadId || conversation.contact) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {conversation.leadId && (
                <Button asChild variant="outline" size="xs">
                  <Link to="/leads/$leadId" params={{ leadId: conversation.leadId }}>
                    Ficha do lead
                    <ExternalLink />
                  </Link>
                </Button>
              )}
              {conversation.contact && (
                <Button asChild variant="outline" size="xs">
                  <Link to="/contatos/$contactId" params={{ contactId: conversation.contact.id }}>
                    Ficha do contato
                    <ExternalLink />
                  </Link>
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Atendimento ─────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pt-4">
          <CardTitle>Atendimento</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-3">
          <div className="space-y-1.5">
            <Label htmlFor="conv-assignee" className="text-xs text-muted-foreground">
              Responsável
            </Label>
            <Select
              id="conv-assignee"
              value={conversation.assignee?.id ?? ""}
              disabled={patch.isPending}
              onChange={(e) => patch.mutate({ assigneeId: e.target.value || null })}
            >
              <option value="">Sem responsável (fila)</option>
              {people.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.fullName ?? p.email}
                </option>
              ))}
            </Select>
          </div>

          {/* Em tela grande a prioridade fica no cabeçalho da conversa; aqui
              só aparece onde ele não tem espaço para ela. */}
          <div className="space-y-1.5 md:hidden">
            <Label htmlFor="conv-priority" className="text-xs text-muted-foreground">
              Prioridade
            </Label>
            <PrioritySelect
              id="conv-priority"
              conversationId={conversation.id}
              value={conversation.priority}
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Etapa do funil ──────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pt-4">
          <CardTitle>Etapa do funil</CardTitle>
        </CardHeader>
        <CardContent className="pt-3">
          <ChatStage
            leadId={conversation.leadId}
            contactId={conversation.contact?.id ?? null}
            contactName={conversation.contact?.fullName}
          />
        </CardContent>
      </Card>

      {/* ── Agendamento ─────────────────────────────────────────────────── */}
      {conversation.contact?.id && (
        <Card>
          <CardHeader className="pt-4">
            <div>
              <CardTitle>Agendamento</CardTitle>
              <CardDescription>Marque o horário sem sair da conversa.</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-3">
            <ChatScheduling
              conversationId={conversation.id}
              contactId={conversation.contact.id}
              leadId={conversation.leadId}
            />
          </CardContent>
        </Card>
      )}

      {/* ── Assistente ──────────────────────────────────────────────────── */}
      <AiPanel
        conversationId={conversation.id}
        onUse={(t) => {
          caixa.current?.inserirTexto(t);
          onRascunhoUsado?.();
        }}
      />
    </>
  );
}
